import React, { useEffect, useState } from 'react';
import { BsCheck2Circle, BsFunnel, BsPlusLg, BsXLg } from 'react-icons/bs';
import { Badge, Button, Dropdown, Form, Spinner } from 'react-bootstrap';
import styles from '../../tickets/TicketsFilter.module.css';

const EmbedListFilter = ({ filterOptions: initialOptions, isLoading, filters, onFilterChange }) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [intermediateFilters, setIntermediateFilters] = useState({});
    const [filterOptions, setFilterOptions] = useState(initialOptions);

    useEffect(() => {
        setFilterOptions(initialOptions);
    }, [initialOptions]);

    useEffect(() => {
        if (JSON.stringify(filters) !== JSON.stringify(intermediateFilters)) {
            setIntermediateFilters({ ...filters });
            updateFilterOptions({ ...filters });
        }
    }, [filters]);

    const toggleFilter = (filter, force) => {
        setFilterOptions((prevOptions) => {
            const newOptions = { ...prevOptions };
            newOptions[filter] = {
                ...prevOptions[filter],
                visible: force === undefined ? !prevOptions[filter].visible : force,
            };
            if (!newOptions[filter].visible) {
                setIntermediateFilters((prev) => ({
                    ...prev,
                    [filter]: newOptions[filter].default,
                }));
            }
            return newOptions;
        });
    };

    const updateFilterOptions = (filtersSnapshot) => {
        setFilterOptions((prevOptions) => {
            const newOptions = { ...prevOptions };
            for (const filter of Object.keys(filtersSnapshot)) {
                if (!newOptions[filter]) {
                    continue;
                }
                const value = filtersSnapshot[filter];
                let isVisible =
                    value !== null &&
                    value !== '' &&
                    !(Array.isArray(value) && value.length === 0);

                if (filter === 'created_at_range' && filtersSnapshot.created_at === 'range') {
                    isVisible = true;
                }

                newOptions[filter] = { ...newOptions[filter], visible: isVisible };
            }
            return newOptions;
        });
    };

    const onFilterUpdate = (filter, value) => {
        setIntermediateFilters((prev) => ({
            ...prev,
            [filter]: value,
        }));
        if (filter === 'created_at' && value === 'range') {
            setFilterOptions((prev) => ({
                ...prev,
                created_at_range: { ...prev.created_at_range, visible: true },
            }));
        }
    };

    const applyFilters = () => {
        const cleanedFilters = { ...intermediateFilters };
        const newOptions = { ...filterOptions };

        Object.entries(filterOptions).forEach(([filter, option]) => {
            if (!option.visible) {
                return;
            }
            const value = cleanedFilters[filter];
            const isEmpty =
                value === null || value === '' || (Array.isArray(value) && value.length === 0);
            if (isEmpty) {
                cleanedFilters[filter] = option.default;
                newOptions[filter] = { ...option, visible: false };
            }
        });

        setIntermediateFilters(cleanedFilters);
        setFilterOptions(newOptions);
        onFilterChange(cleanedFilters);
        setShowDropdown(false);
    };

    const clearFilters = () => {
        const defaultFilters = Object.entries(filterOptions).reduce((row, [key, value]) => {
            row[key] = value.default;
            return row;
        }, {});
        setIntermediateFilters(defaultFilters);
        setFilterOptions((prev) =>
            Object.entries(prev).reduce((acc, [key, value]) => {
                acc[key] = { ...value, visible: false };
                return acc;
            }, {})
        );
    };

    const visibleFiltersCount = Object.values(filterOptions).filter((v) => v.visible).length;

    const renderFilterControl = (filter, value) => {
        if (filter === 'created_at') {
            return (
                <Form.Control
                    as="select"
                    value={value ?? ''}
                    onChange={(e) => onFilterUpdate(filter, e.target.value)}
                >
                    <option value="">Any time</option>
                    <option value="today">Today</option>
                    <option value="this_week">This week</option>
                    <option value="this_month">This month</option>
                    <option value="range">Custom range</option>
                </Form.Control>
            );
        }
        if (filter === 'created_at_range') {
            const range = Array.isArray(value) ? value : ['', ''];
            return (
                <div className="d-flex gap-2">
                    <Form.Control
                        type="date"
                        value={range[0] ?? ''}
                        onChange={(e) =>
                            onFilterUpdate(filter, [e.target.value, range[1] ?? ''])
                        }
                    />
                    <Form.Control
                        type="date"
                        value={range[1] ?? ''}
                        onChange={(e) =>
                            onFilterUpdate(filter, [range[0] ?? '', e.target.value])
                        }
                    />
                </div>
            );
        }
        if (filter === 'display_mode') {
            const selected = Array.isArray(value) ? value : [];
            return (
                <Form.Control
                    as="select"
                    multiple
                    value={selected}
                    onChange={(e) => {
                        const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                        onFilterUpdate(filter, opts);
                    }}
                    style={{ minHeight: 72 }}
                >
                    <option value="inline">Inline</option>
                    <option value="floating">Floating</option>
                </Form.Control>
            );
        }
        return null;
    };

    return (
        <Dropdown
            show={showDropdown}
            onToggle={(isOpen) => setShowDropdown(isOpen)}
            autoClose="outside"
            drop="down"
            className="tickets-filters-dropdown"
        >
            <Dropdown.Toggle variant="secondary" onClick={() => setShowDropdown(!showDropdown)}>
                <BsFunnel />
                {visibleFiltersCount > 0 && (
                    <Badge bg="info" className="ms-1">
                        {visibleFiltersCount}
                    </Badge>
                )}
            </Dropdown.Toggle>
            <Dropdown.Menu
                align="end"
                style={{ fontSize: '14px', minWidth: '300px' }}
                popperConfig={{ modifiers: [{ name: 'flip', enabled: false }] }}
            >
                <Dropdown.Header className={styles.filtersHeader}>
                    Filters:
                    <Dropdown>
                        <Dropdown.Toggle variant="secondary">
                            <BsPlusLg />
                        </Dropdown.Toggle>
                        <Dropdown.Menu style={{ fontSize: '14px' }}>
                            <Dropdown.Header>Add filter:</Dropdown.Header>
                            {Object.entries(filterOptions)
                                .filter(([filter]) => filter !== 'created_at_range')
                                .map(([filter, opt], index) =>
                                    !opt.visible ? (
                                        <Dropdown.Item key={index} onClick={() => toggleFilter(filter)}>
                                            {opt.label}
                                        </Dropdown.Item>
                                    ) : null
                                )}
                        </Dropdown.Menu>
                    </Dropdown>
                </Dropdown.Header>
                {isLoading ? (
                    <Spinner animation="border" />
                ) : (
                    <>
                        {Object.values(filterOptions).some((v) => v.visible) ? (
                            Object.entries(filterOptions).map(([filter, opt], index) =>
                                opt.visible ? (
                                    <div key={index} className={styles.filterItemContainer}>
                                        <div style={{ flex: 1 }}>
                                            <div className="small text-muted mb-1">{opt.label}</div>
                                            {renderFilterControl(
                                                filter,
                                                intermediateFilters[filter]
                                            )}
                                        </div>
                                        {filter !== 'created_at_range' && (
                                            <BsXLg
                                                role="button"
                                                onClick={() => toggleFilter(filter)}
                                            />
                                        )}
                                    </div>
                                ) : null
                            )
                        ) : (
                            <div className={styles.noFilterMessage}>No filters applied.</div>
                        )}
                    </>
                )}
                <Dropdown.Divider />
                <div className={styles.buttonContainer}>
                    <Button variant="secondary" onClick={clearFilters}>
                        Clear All
                    </Button>
                    <Button variant="primary" onClick={applyFilters}>
                        <BsCheck2Circle /> Apply
                    </Button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export const EMBED_FORMS_ADVANCED_FILTER_OPTIONS = {
    created_at: { label: 'Created date', visible: false, default: '' },
    created_at_range: { label: 'Created range', visible: false, default: [] },
    display_mode: { label: 'Display mode', visible: false, default: [] },
};

export const EMBED_DOMAINS_ADVANCED_FILTER_OPTIONS = {
    created_at: { label: 'Created date', visible: false, default: '' },
    created_at_range: { label: 'Created range', visible: false, default: [] },
};

export default EmbedListFilter;
